/**
 * POS Cart state management — useReducer-based hook.
 * Cart items + coupon persist in sessionStorage (survives refresh, clears on tab close).
 * Customer always comes from profile default (never persisted).
 * Plan: 125-pos-react-ui, Epic 02 + Epic 06
 * Coupon state: Plan 133, Epic 02
 */

import { useReducer, useMemo, useCallback, useEffect } from "react";
import type { POSItem, POSCartItem, POSTaxDetail, AppliedCoupon } from "../types";

// --- sessionStorage ---

const CART_STORAGE_VERSION = 3; // v3: added line_id for per-unit bundle config

function getCartStorageKey(posProfile: string): string {
  return `pos_cart_${posProfile}`;
}

function loadCartState(posProfile: string): {
  items: POSCartItem[];
  coupon: AppliedCoupon | null;
} {
  try {
    const saved = sessionStorage.getItem(getCartStorageKey(posProfile));
    if (!saved) return { items: [], coupon: null };
    const parsed = JSON.parse(saved);
    if (parsed._v === CART_STORAGE_VERSION && Array.isArray(parsed.items)) {
      return { items: parsed.items, coupon: parsed.coupon ?? null };
    }
  } catch {
    /* corrupted data, ignore */
  }
  return { items: [], coupon: null };
}

function saveCartState(
  posProfile: string,
  items: POSCartItem[],
  coupon: AppliedCoupon | null,
): void {
  try {
    sessionStorage.setItem(
      getCartStorageKey(posProfile),
      JSON.stringify({ _v: CART_STORAGE_VERSION, items, coupon }),
    );
  } catch {
    /* sessionStorage full or unavailable */
  }
}

// --- Actions ---

type CartAction =
  | { type: "ADD_ITEM"; item: POSItem; variant_attributes?: string; line_id?: string }
  | { type: "SET_QTY"; item_code: string; qty: number; line_id?: string }
  | { type: "REMOVE_ITEM"; item_code: string; line_id?: string }
  | { type: "CLEAR" }
  | { type: "SET_CUSTOMER"; customer: string; customer_name: string }
  | { type: "APPLY_COUPON"; coupon: AppliedCoupon }
  | { type: "REMOVE_COUPON" };

interface CartState {
  items: POSCartItem[];
  customer: { name: string; customer_name: string };
  coupon: AppliedCoupon | null;
}

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "ADD_ITEM": {
      const { item } = action;
      const rate = item.price_list_rate ?? 0;

      // line_id present → always add as new row (configurable bundle)
      if (action.line_id) {
        return {
          ...state,
          items: [
            ...state.items,
            {
              item_code: item.item_code,
              item_name: item.item_name,
              rate,
              qty: 1,
              uom: item.uom,
              item_image: item.item_image,
              is_stock_item: item.is_stock_item,
              amount: rate,
              variant_attributes: action.variant_attributes,
              line_id: action.line_id,
            },
          ],
        };
      }

      // Default: merge by item_code (POS behavior) — but never into a row that
      // has its own line_id (bundle / personalized unit): those carry per-unit
      // data, so a plain re-add must not inherit it (TKT-2026-00150).
      const isPlainMatch = (i: POSCartItem) =>
        i.item_code === item.item_code && !i.line_id;
      const existing = state.items.find(isPlainMatch);
      if (existing) {
        const newQty = existing.qty + 1;
        return {
          ...state,
          items: state.items.map((i) =>
            isPlainMatch(i)
              ? { ...i, qty: newQty, amount: rate * newQty }
              : i,
          ),
        };
      }
      return {
        ...state,
        items: [
          ...state.items,
          {
            item_code: item.item_code,
            item_name: item.item_name,
            rate,
            qty: 1,
            uom: item.uom,
            item_image: item.item_image,
            is_stock_item: item.is_stock_item,
            amount: rate,
            variant_attributes: action.variant_attributes,
          },
        ],
      };
    }
    case "SET_QTY": {
      const matchQty = action.line_id
        ? (i: POSCartItem) => i.line_id === action.line_id
        : (i: POSCartItem) => i.item_code === action.item_code;
      if (action.qty <= 0) {
        return { ...state, items: state.items.filter((i) => !matchQty(i)) };
      }
      return {
        ...state,
        items: state.items.map((i) =>
          matchQty(i)
            ? { ...i, qty: action.qty, amount: i.rate * action.qty }
            : i,
        ),
      };
    }
    case "REMOVE_ITEM": {
      const matchRm = action.line_id
        ? (i: POSCartItem) => i.line_id === action.line_id
        : (i: POSCartItem) => i.item_code === action.item_code;
      return { ...state, items: state.items.filter((i) => !matchRm(i)) };
    }
    case "CLEAR":
      return { ...state, items: [], coupon: null };
    case "SET_CUSTOMER":
      return {
        ...state,
        customer: {
          name: action.customer,
          customer_name: action.customer_name,
        },
      };
    case "APPLY_COUPON":
      return { ...state, coupon: action.coupon };
    case "REMOVE_COUPON":
      return { ...state, coupon: null };
    default:
      return state;
  }
}

// --- Tax computation ---

/**
 * Compute tax on cart subtotal, optionally accounting for a discount.
 * When applyDiscountOn is "Net Total", tax is computed on (subtotal - discount).
 * When "Grand Total" (default), tax is computed on the full subtotal.
 */
function computeTax(
  subtotal: number,
  taxes: POSTaxDetail[],
  discountAmount = 0,
  applyDiscountOn: "Grand Total" | "Net Total" = "Grand Total",
): number {
  const taxBase =
    applyDiscountOn === "Net Total" ? subtotal - discountAmount : subtotal;
  let taxAmount = 0;
  for (const tax of taxes) {
    if (tax.included_in_print_rate === 1) continue; // already in price
    if (tax.charge_type === "On Net Total") {
      taxAmount += taxBase * (tax.rate / 100);
    } else {
      // Actual, On Previous Row — not supported in POS UI yet
      if (typeof console !== "undefined") {
        console.warn(
          `[POS] Unsupported tax charge_type "${tax.charge_type}" — skipping`,
        );
      }
    }
  }
  return Math.round(taxAmount * 100) / 100;
}

// --- Hook ---

export function useCart(
  defaultCustomer: string,
  defaultCustomerName: string,
  taxes: POSTaxDetail[],
  posProfile: string,
) {
  const [state, dispatch] = useReducer(
    cartReducer,
    { posProfile, defaultCustomer, defaultCustomerName },
    (init) => {
      const { items, coupon } = loadCartState(init.posProfile);
      return {
        items,
        coupon,
        customer: {
          name: init.defaultCustomer,
          customer_name: init.defaultCustomerName,
        },
      };
    },
  );

  // Persist items + coupon to sessionStorage on every change
  useEffect(() => {
    saveCartState(posProfile, state.items, state.coupon);
  }, [state.items, state.coupon, posProfile]);

  const subtotal = useMemo(
    () => state.items.reduce((sum, i) => sum + i.amount, 0),
    [state.items],
  );

  const discountAmount = useMemo(
    () => state.coupon?.discount_amount ?? 0,
    [state.coupon],
  );

  const taxAmount = useMemo(
    () =>
      computeTax(
        subtotal,
        taxes,
        discountAmount,
        state.coupon?.apply_discount_on,
      ),
    [subtotal, taxes, discountAmount, state.coupon?.apply_discount_on],
  );

  const total = useMemo(() => {
    let raw: number;
    if (state.coupon?.apply_discount_on === "Net Total") {
      // Discount already subtracted from tax base
      raw = subtotal - discountAmount + taxAmount;
    } else {
      // Grand Total (default): subtract discount from final total
      raw = subtotal + taxAmount - discountAmount;
    }
    // Clamp to 0 — prevent negative totals from oversized discounts
    return Math.max(0, Math.round(raw * 100) / 100);
  }, [subtotal, taxAmount, discountAmount, state.coupon?.apply_discount_on]);

  const itemCount = useMemo(
    () => state.items.reduce((sum, i) => sum + i.qty, 0),
    [state.items],
  );

  const addItem = useCallback(
    (item: POSItem, variant_attributes?: string, line_id?: string) =>
      dispatch({ type: "ADD_ITEM", item, variant_attributes, line_id }),
    [],
  );
  const setQty = useCallback(
    (item_code: string, qty: number, line_id?: string) =>
      dispatch({ type: "SET_QTY", item_code, qty, line_id }),
    [],
  );
  const removeItem = useCallback(
    (item_code: string, line_id?: string) =>
      dispatch({ type: "REMOVE_ITEM", item_code, line_id }),
    [],
  );
  const clearCart = useCallback(() => dispatch({ type: "CLEAR" }), []);
  const setCustomer = useCallback(
    (customer: string, customer_name: string) =>
      dispatch({ type: "SET_CUSTOMER", customer, customer_name }),
    [],
  );
  const applyCoupon = useCallback(
    (coupon: AppliedCoupon) => dispatch({ type: "APPLY_COUPON", coupon }),
    [],
  );
  const removeCoupon = useCallback(
    () => dispatch({ type: "REMOVE_COUPON" }),
    [],
  );

  return {
    items: state.items,
    customer: state.customer,
    coupon: state.coupon,
    subtotal,
    discountAmount,
    taxAmount,
    total,
    itemCount,
    addItem,
    setQty,
    removeItem,
    clearCart,
    setCustomer,
    applyCoupon,
    removeCoupon,
  };
}
